import { config } from '@/forms/config';
import { ValidationAdapter } from '@/forms/core';
import type {
  AtLeastOneChildWithValueRestriction,
  FieldsGroupRestrictionPayload,
  FormConditionalValidation,
  FormField,
  FormInitialValues,
  FormValidations,
  FormValidationSchema,
  RequiredWhenValueRestriction,
} from '@/forms/typings';
import { registerAtLeastOneChildAnsweredYesValidation } from './createAtLeastOneChildAnsweredYesYup';
import { registerLimitPairsValidation } from './createLimitPairsYup';
import {
  createRequiredWhenValueYupSchema,
  requiredWhenValueKey,
} from './createRequiredWhenValueYup';
import { formFieldHasFormikValue } from './formFieldHasFormikValue';
import { generateConditionalValidation } from './generateConditionalValidation';
import { generateConditionalValidationFieldIds } from './generateConditionalValidationFieldIds';

/** Whitespace-only text is no answer, as the server's IsNullOrWhiteSpace reads it. */
const childHasValue = (
  child: FormField,
  root: Record<string, unknown>,
  treatNotApplicableAsValue: boolean | undefined,
): boolean => {
  const own = root[child.fieldId];
  const blankText = typeof own === 'string' && own.trim() === '';

  return formFieldHasFormikValue(
    child,
    blankText ? { ...root, [child.fieldId]: '' } : root,
    { treatNotApplicableAsValue },
  );
};

/** The children an AtLeastOneChildWithValue rule reads: those `childItemIDs` lists, else every child. */
export const atLeastOneChildWithValueChildren = (
  groupFieldId: string,
  children: FormField[],
  restriction: AtLeastOneChildWithValueRestriction,
): FormField[] => {
  const { childItemIDs } = restriction;

  if (!childItemIDs) return children;

  // groupFieldId already carries the entry index, as RequiredWhenValue resolves its children.
  return children.filter(child =>
    childItemIDs.some(itemID => child.fieldId === `${groupFieldId}_${itemID}`),
  );
};

/**
 * Yup schema for a non-editable Formik key that validates that at least one of
 * the group's children has data — only the listed children when `childItemIDs`
 * is set. Gated by the group's visibility conditions (should-validate, QWD
 * branch, N/A) so hidden subtrees never block submit.
 */
export const createAtLeastOneChildWithValueYupSchema = (
  children: FormField[],
  restriction: AtLeastOneChildWithValueRestriction,
  conditions: FormConditionalValidation[] | undefined,
  groupFieldId: string,
) => {
  const Yup = ValidationAdapter.getLibrary();
  const message =
    restriction.message?.trim() ||
    'At least one of these fields must be filled in.';

  const candidates = atLeastOneChildWithValueChildren(
    groupFieldId,
    children,
    restriction,
  );

  const base = Yup.mixed().nullable();

  const tested = base.test({
    name: 'atLeastOneChildWithValue',
    message,
    test() {
      const root = this.parent as Record<string, unknown> | undefined;
      if (!root) return true;

      return candidates.some(child =>
        childHasValue(child, root, restriction.treatNotApplicableAsValue),
      );
    },
  });

  if (!conditions?.length) return tested;

  return base.when(
    generateConditionalValidationFieldIds(conditions),
    (values, schema) =>
      generateConditionalValidation(conditions, values) ? tested : schema,
  );
};

/** The synthetic Formik key for a group's "at least one child with value" rule. */
export const atLeastOneChildWithValueKey = (groupFieldId: string): string =>
  `${groupFieldId}${config.atLeastOneChildWithValueFieldSuffix}`;

/**
 * Registers the synthetic "at least one child with value" Formik key (seeded
 * undefined so it is touched on submit) and its gated schema, for one group.
 */
export const registerAtLeastOneChildWithValueValidation = (
  initialValues: FormInitialValues,
  validationFields: FormValidations,
  groupFieldId: string,
  children: FormField[],
  restriction: AtLeastOneChildWithValueRestriction,
  conditions: FormConditionalValidation[] | undefined,
): void => {
  const key = atLeastOneChildWithValueKey(groupFieldId);
  initialValues[key] = undefined;
  validationFields[key] = createAtLeastOneChildWithValueYupSchema(
    children,
    restriction,
    conditions,
    groupFieldId,
  ) as FormValidationSchema;
};

/**
 * Registers the synthetic "required when value" Formik key (seeded undefined
 * so it is touched on submit) and its gated schema, for one group.
 */
export const registerRequiredWhenValueValidation = (
  initialValues: FormInitialValues,
  validationFields: FormValidations,
  groupFieldId: string,
  children: FormField[],
  restriction: RequiredWhenValueRestriction,
  conditions: FormConditionalValidation[] | undefined,
): void => {
  const key = requiredWhenValueKey(groupFieldId);
  initialValues[key] = undefined;
  validationFields[key] = createRequiredWhenValueYupSchema(
    children,
    restriction,
    groupFieldId,
    conditions,
  ) as FormValidationSchema;
};

/**
 * Registers Formik validations for every rule present on a group restriction.
 * Single dispatch point — adding a new restriction kind only extends this.
 */
export const registerGroupRestrictionValidations = (
  initialValues: FormInitialValues,
  validationFields: FormValidations,
  groupFieldId: string,
  children: FormField[],
  restriction: FieldsGroupRestrictionPayload | undefined,
  conditions: FormConditionalValidation[] | undefined,
): void => {
  if (!restriction) return;

  if (restriction.atLeastOneChildWithValue) {
    registerAtLeastOneChildWithValueValidation(
      initialValues,
      validationFields,
      groupFieldId,
      children,
      restriction.atLeastOneChildWithValue,
      conditions,
    );
  }

  if (restriction.atLeastOneChildAnsweredYes) {
    registerAtLeastOneChildAnsweredYesValidation(
      initialValues,
      validationFields,
      groupFieldId,
      children,
      restriction.atLeastOneChildAnsweredYes,
      conditions,
    );
  }

  if (restriction.requiredWhenValue) {
    registerRequiredWhenValueValidation(
      initialValues,
      validationFields,
      groupFieldId,
      children,
      restriction.requiredWhenValue,
      conditions,
    );
  }

  if (restriction.limitPairs) {
    registerLimitPairsValidation(
      initialValues,
      validationFields,
      groupFieldId,
      children,
      restriction.limitPairs,
      conditions,
    );
  }
};
